import moment from "moment-timezone";
import { call, put, select, takeEvery } from "redux-saga/effects";
import { ENDPOINT, PLOT } from "../constants";
import { getDataFromKpiDash } from "../api";
import { setPlotData, setPlotError, setPlotLoading } from "../actions";

const S3_BUCKET_BASE_URL = process.env.REACT_APP_S3_BUCKET_BASE_URL || "";
const DEFAULT_FETCH_DAYS = 30;

const safeParseRows = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  const body = response?.body ?? response?.data ?? response?.rows ?? response;
  if (Array.isArray(body)) {
    return body;
  }

  if (typeof body === "string") {
    try {
      const parsed = JSON.parse(body);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  return [];
};

const normalizeRange = (dateRange) => {
  if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
    return [
      moment(dateRange[0]).tz("Asia/Tokyo").format("YYYY-MM-DD"),
      moment(dateRange[1]).tz("Asia/Tokyo").format("YYYY-MM-DD"),
    ];
  }

  return [
    moment().tz("Asia/Tokyo").subtract(DEFAULT_FETCH_DAYS, "days").format("YYYY-MM-DD"),
    moment().tz("Asia/Tokyo").format("YYYY-MM-DD"),
  ];
};

const escapeSql = (value) => String(value ?? "").replace(/'/g, "''");

const toText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object" && value !== null) {
    if (Object.prototype.hasOwnProperty.call(value, "stringValue")) {
      return String(value.stringValue ?? "");
    }
    if (Object.prototype.hasOwnProperty.call(value, "numberValue")) {
      return String(value.numberValue ?? "");
    }
  }

  return String(value);
};

const toNumber = (value) => {
  const numeric = Number(toText(value));
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  return moment(value).tz("Asia/Tokyo").format("YYYY-MM-DD");
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  return moment(value).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
};

const buildThumbUrl = (objectKey) => {
  if (!objectKey || !S3_BUCKET_BASE_URL) {
    return "";
  }

  return `${S3_BUCKET_BASE_URL}/${objectKey}`;
};

const buildPlotQuery = (rangeTarget) => {
  const [dateFrom, dateTo] = normalizeRange(rangeTarget);
  const utcFrom = moment.tz(dateFrom, "YYYY-MM-DD", "Asia/Tokyo")
    .startOf("day")
    .utc()
    .format("YYYY-MM-DD HH:mm:ss");
  const utcToExclusive = moment.tz(dateTo, "YYYY-MM-DD", "Asia/Tokyo")
    .add(1, "day")
    .startOf("day")
    .utc()
    .format("YYYY-MM-DD HH:mm:ss");

  return {
    query: `
      WITH plot_base AS (
        SELECT
          pp.id AS plot_photography_id,
          pp.plot_game_id,
          pgi.plot_game_name,
          pp.area_id,
          gafd.name AS municipality_name,
          pp.plot_id,
          pp.player_id,
          pp.photographed_datetime,
          pp.longitude,
          pp.latitude,
          p.asset_type,
          p.asset_sub_type,
          p.confidence_score,
          patn.name AS asset_type_name,
          pp2.id AS plot_photo_id,
          pp2.file_name,
          pp2.object_key,
          ROW_NUMBER() OVER (
            PARTITION BY pp.plot_id, pp.player_id, COALESCE(pp2.id, pp.id)
            ORDER BY pp.photographed_datetime DESC, pp.id DESC, pp2.id DESC
          ) AS rn
        FROM plot_photography pp
        INNER JOIN plot p
          ON p.id = pp.plot_id
        LEFT JOIN plot_game_info pgi
          ON pgi.plot_game_id = pp.plot_game_id
         AND pgi.language_code = 'JA'
        LEFT JOIN game_field_area_display_name gafd
          ON gafd.area_id = pp.area_id
         AND gafd.language_code = 'JA'
        LEFT JOIN power_asset_type_name patn
          ON patn.power_asset_type = p.asset_type
         AND patn.power_asset_sub_type = p.asset_sub_type
         AND patn.language_code = 'JA'
        LEFT JOIN plot_photo pp2
          ON pp2.plot_photography_id = pp.id
        WHERE pp.photographed_datetime >= '${escapeSql(utcFrom)}'
          AND pp.photographed_datetime < '${escapeSql(utcToExclusive)}'
      )
      SELECT *
      FROM plot_base
      WHERE rn = 1
      ORDER BY photographed_datetime DESC, plot_id DESC, player_id ASC, plot_photo_id DESC;
    `,
    type: "select",
  };
};

const normalizePlotRows = (rows) => {
  const plots = new Map();

  rows.forEach((row) => {
    const plotPhotographyId = Array.isArray(row)
      ? toText(row[0])
      : toText(row?.plot_photography_id);
    const plotGameId = Array.isArray(row)
      ? toText(row[1])
      : toText(row?.plot_game_id);
    const plotGameName = Array.isArray(row)
      ? toText(row[2])
      : toText(row?.plot_game_name);
    const areaId = Array.isArray(row)
      ? toText(row[3])
      : toText(row?.area_id);
    const municipalityName = Array.isArray(row)
      ? toText(row[4])
      : toText(row?.municipality_name);
    const plotId = Array.isArray(row)
      ? toText(row[5])
      : toText(row?.plot_id);
    const playerId = Array.isArray(row)
      ? toText(row[6])
      : toText(row?.player_id);
    const photographedDatetime = Array.isArray(row)
      ? toText(row[7])
      : toText(row?.photographed_datetime);
    const longitude = Array.isArray(row)
      ? toNumber(row[8])
      : toNumber(row?.longitude);
    const latitude = Array.isArray(row)
      ? toNumber(row[9])
      : toNumber(row?.latitude);
    const confidenceScore = Array.isArray(row)
      ? toNumber(row[10])
      : toNumber(row?.confidence_score);
    const assetTypeCode = Array.isArray(row)
      ? toText(row[11])
      : toText(row?.asset_type);
    const assetSubType = Array.isArray(row)
      ? toText(row[12])
      : toText(row?.asset_sub_type);
    const assetTypeName = Array.isArray(row)
      ? toText(row[13])
      : toText(row?.asset_type_name);
    const plotPhotoId = Array.isArray(row)
      ? toText(row[14])
      : toText(row?.plot_photo_id);
    const fileName = Array.isArray(row)
      ? toText(row[15])
      : toText(row?.file_name);
    const objectKey = Array.isArray(row)
      ? toText(row[16])
      : toText(row?.object_key);

    if (!plotId) {
      return;
    }

    const assetType = assetTypeName || [assetTypeCode, assetSubType].filter(Boolean).join("|") || "不明";
    const plotKey = plotId;
    const target = plots.get(plotKey) || {
      id: plotKey,
      plotId: plotKey,
      name: "",
      eventId: plotGameId,
      event: plotGameName || plotGameId || "不明",
      municipalityId: areaId,
      municipality: municipalityName || areaId || "不明",
      date: formatDate(photographedDatetime),
      photographedAt: formatDateTime(photographedDatetime),
      lv: confidenceScore >= 100 ? 4 : confidenceScore >= 60 ? 3 : confidenceScore >= 40 ? 2 : 1,
      confidenceScore,
      assetType,
      assetTypeCode,
      assetSubType,
      lng: longitude,
      lat: latitude,
      photos: [],
      _photoIndex: new Set(),
      _sortKey: photographedDatetime ? new Date(photographedDatetime).getTime() : 0,
    };

    if (!plots.has(plotKey)) {
      target.name = `${target.municipality} ${target.assetType}`.trim();
      plots.set(plotKey, target);
    }

    const photoKey = plotPhotoId || fileName || plotPhotographyId || `${plotKey}:${playerId}`;
    if (photoKey && !target._photoIndex.has(photoKey)) {
      target._photoIndex.add(photoKey);
      target.photos.push({
        id: photoKey,
        fileName,
        objectKey,
        thumbUrl: buildThumbUrl(objectKey),
        label: playerId || "撮影者",
        photographerId: playerId,
        capturedAt: formatDateTime(photographedDatetime),
      });
    }
  });

  return Array.from(plots.values())
    .map((plot) => ({
      id: plot.id,
      plotId: plot.plotId,
      name: plot.name,
      eventId: plot.eventId,
      event: plot.event,
      municipalityId: plot.municipalityId,
      municipality: plot.municipality,
      date: plot.date,
      photographedAt: plot.photographedAt,
      lv: plot.lv,
      confidenceScore: plot.confidenceScore,
      assetType: plot.assetType,
      assetTypeCode: plot.assetTypeCode,
      assetSubType: plot.assetSubType,
      lng: plot.lng,
      lat: plot.lat,
      photos: plot.photos,
    }))
    .sort((a, b) => b.photographedAt.localeCompare(a.photographedAt));
};

export function* handlePlotData(action) {
  yield put(setPlotLoading(true));
  yield put(setPlotError(""));

  try {
    const rangeTarget = action?.payload?.dateRange || (yield select((state) => state.app.dateRange));
    const sqlQueryPlotData = buildPlotQuery(rangeTarget);
    const response = yield call(getDataFromKpiDash, sqlQueryPlotData, ENDPOINT.GET_DATA_API_REPLICA);
    const rows = safeParseRows(response);
    const normalizedRows = normalizePlotRows(rows);
    yield put(setPlotData(normalizedRows));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[plotSaga] failed to load plot data", error);
    yield put(setPlotData([]));
    yield put(setPlotError("プロットデータ取得に失敗しました。"));
  } finally {
    yield put(setPlotLoading(false));
  }
}

export default function* plotSaga() {
  yield takeEvery(PLOT.GET_PLOT_DATA, handlePlotData);
}
